import React, { useEffect, useMemo, useState } from "react";
import { Copy, Link2, PlusCircle, Save } from "lucide-react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import { copyToClipboard, formatDateTime, getRoleLabel } from "../lib/utils";

const initialForm = {
  name: "",
  email: "",
  password: "",
  role: "driver",
  status: "active",
  onboarding_status: "invited",
  license_number: "",
  school_name: "",
  address: "",
  contact_phone: "",
  contact_email: "",
  parent_phone: "",
};

export default function UsersPage() {
  const { users, loadUsers, createUser, updateUser, generateResetLink } = useData();
  const [form, setForm] = useState(initialForm);
  const [editingUser, setEditingUser] = useState(null);
  const [resetPayload, setResetPayload] = useState(null);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const summary = useMemo(() => {
    const counts = { admin: 0, driver: 0, school: 0, parent: 0 };
    users.forEach((entry) => {
      counts[entry.role] = (counts[entry.role] || 0) + 1;
    });
    return counts;
  }, [users]);

  const resetForm = () => {
    setEditingUser(null);
    setForm(initialForm);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setForm({
      name: user.name || "",
      email: user.email || "",
      password: "",
      role: user.role || "driver",
      status: user.status || "active",
      onboarding_status: user.onboarding_status || "active",
      license_number: user.profile?.license_number || "",
      school_name: user.profile?.name || "",
      address: user.profile?.address || "",
      contact_phone: user.profile?.contact_phone || "",
      contact_email: user.profile?.contact_email || "",
      parent_phone: user.profile?.phone || "",
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!editingUser && !form.password) {
      toast.error("Enter a temporary password for the new user.");
      return;
    }
    const payload = { ...form };
    if (!payload.password) delete payload.password;
    const result = editingUser ? await updateUser(editingUser.id, payload) : await createUser(payload);
    if (result.success) {
      toast.success(editingUser ? "User updated." : "User created.");
      resetForm();
      loadUsers();
    } else {
      toast.error(result.error);
    }
  };

  const handleGenerateReset = async (userId) => {
    const result = await generateResetLink(userId);
    if (result.success) {
      setResetPayload({
        ...result.data,
        absoluteUrl: `${window.location.origin}${result.data.reset_url}`,
      });
      toast.success("Reset link generated.");
    } else {
      toast.error(result.error);
    }
  };

  const copyResetLink = async () => {
    const copied = await copyToClipboard(resetPayload?.absoluteUrl);
    if (copied) toast.success("Reset link copied.");
    else toast.error("Unable to copy reset link.");
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="grid gap-4 md:grid-cols-4">
        {Object.entries(summary).map(([role, count]) => (
          <div key={role} className="guardian-card bg-white p-5">
            <p className="text-sm text-[#64748B]">{getRoleLabel(role)}</p>
            <p className="mt-1 text-3xl font-semibold text-[#0F172A]">{count}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <form onSubmit={handleSubmit} className="guardian-card bg-white p-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">{editingUser ? "Edit user" : "Add user"}</h2>
              <p className="mt-1 text-sm text-[#64748B]">Create staff and family access with role-based permissions.</p>
            </div>
            {editingUser ? <button type="button" className="text-sm font-medium text-[#163D6B]" onClick={resetForm}>Cancel</button> : null}
          </div>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <div>
              <label className="text-sm font-medium text-[#0F172A]">Name</label>
              <input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#0F172A]">Email</label>
              <input value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#0F172A]">Password {editingUser ? "(leave blank to keep current)" : ""}</label>
              <input type="password" value={form.password} placeholder={editingUser ? "Leave blank to keep current password" : "Set a temporary password"} onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm" />
              {!editingUser && <p className="mt-2 text-xs text-[#64748B]">New users will be required to change this password on first sign-in.</p>}
            </div>
            <div>
              <label className="text-sm font-medium text-[#0F172A]">Role</label>
              <select value={form.role} onChange={(event) => setForm((current) => ({ ...current, role: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm">
                <option value="admin">Admin</option>
                <option value="driver">Driver</option>
                <option value="school">School</option>
                <option value="parent">Parent</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#0F172A]">Status</label>
              <select value={form.status} onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#0F172A]">Onboarding status</label>
              <select value={form.onboarding_status} onChange={(event) => setForm((current) => ({ ...current, onboarding_status: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm">
                <option value="invited">Invited</option>
                <option value="active">Active</option>
              </select>
            </div>
            {form.role === "driver" && (
              <div>
                <label className="text-sm font-medium text-[#0F172A]">Licence number</label>
                <input value={form.license_number} onChange={(event) => setForm((current) => ({ ...current, license_number: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm" />
              </div>
            )}
            {form.role === "school" && (
              <>
                <div>
                  <label className="text-sm font-medium text-[#0F172A]">School name</label>
                  <input value={form.school_name} onChange={(event) => setForm((current) => ({ ...current, school_name: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#0F172A]">School phone</label>
                  <input value={form.contact_phone} onChange={(event) => setForm((current) => ({ ...current, contact_phone: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-[#0F172A]">Address</label>
                  <input value={form.address} onChange={(event) => setForm((current) => ({ ...current, address: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm" />
                </div>
              </>
            )}
            {form.role === "parent" && (
              <>
                <div>
                  <label className="text-sm font-medium text-[#0F172A]">Parent phone</label>
                  <input value={form.parent_phone} onChange={(event) => setForm((current) => ({ ...current, parent_phone: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-[#0F172A]">Address</label>
                  <input value={form.address} onChange={(event) => setForm((current) => ({ ...current, address: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm" />
                </div>
              </>
            )}
          </div>
          <button type="submit" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#163D6B] px-4 py-3 text-sm font-semibold text-white">
            {editingUser ? <Save size={16} /> : <PlusCircle size={16} />}
            {editingUser ? "Save changes" : "Create user"}
          </button>
        </form>

        <div className="space-y-6">
          {resetPayload && (
            <div className="guardian-card border border-emerald-200 bg-emerald-50 p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-semibold text-emerald-900">Password reset link ready</p>
                  <p className="mt-1 text-sm text-emerald-800">Share this secure link with {resetPayload.user?.email}. It remains valid until {formatDateTime(resetPayload.expires_at)}.</p>
                </div>
                <button onClick={copyResetLink} className="inline-flex items-center gap-2 rounded-xl border border-emerald-300 bg-white px-3 py-2 text-sm font-medium text-emerald-800">
                  <Copy size={14} />
                  Copy
                </button>
              </div>
              <div className="mt-4 rounded-2xl border border-emerald-200 bg-white p-4 text-xs text-[#0F172A] break-all">
                {resetPayload.absoluteUrl}
              </div>
            </div>
          )}

          <div className="guardian-card overflow-hidden bg-white">
            <div className="border-b border-[#E2E8F0] px-6 py-4">
              <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">User directory</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-[#F8FAFC] text-left text-[#64748B]">
                  <tr>
                    <th className="px-6 py-3 font-medium">User</th>
                    <th className="px-6 py-3 font-medium">Role</th>
                    <th className="px-6 py-3 font-medium">Status</th>
                    <th className="px-6 py-3 font-medium">Onboarding</th>
                    <th className="px-6 py-3 font-medium">Created</th>
                    <th className="px-6 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length ? (
                    users.map((user) => (
                      <tr key={user.id} className="border-t border-[#E2E8F0] align-top">
                        <td className="px-6 py-4">
                          <p className="font-medium text-[#0F172A]">{user.name}</p>
                          <p className="mt-1 text-xs text-[#64748B]">{user.email}</p>
                        </td>
                        <td className="px-6 py-4 text-[#475569]">{getRoleLabel(user.role)}</td>
                        <td className="px-6 py-4 text-[#475569]">{user.status}</td>
                        <td className="px-6 py-4 text-[#475569]">{user.onboarding_status || "active"}</td>
                        <td className="px-6 py-4 text-[#475569]">{formatDateTime(user.created_at)}</td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-2">
                            <button onClick={() => handleEdit(user)} className="rounded-lg border border-[#E2E8F0] px-3 py-2 text-xs font-medium text-[#0F172A]">Edit</button>
                            <button onClick={() => handleGenerateReset(user.id)} className="inline-flex items-center gap-1 rounded-lg border border-[#E2E8F0] px-3 py-2 text-xs font-medium text-[#0F172A]">
                              <Link2 size={12} /> Reset link
                            </button>
                            <button onClick={() => updateUser(user.id, { status: user.status === "active" ? "inactive" : "active" }).then(() => loadUsers())} className="rounded-lg border border-[#E2E8F0] px-3 py-2 text-xs font-medium text-[#0F172A]">
                              {user.status === "active" ? "Deactivate" : "Activate"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center text-[#64748B]">No users available.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
